import React, { useState, useEffect } from "react";
import axios from "axios";
import { useBusinessPlan } from "../../App";
import { useCurrency } from "../../contexts/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import ProductFormDialog from "../shared/ProductFormDialog";
import { Tooltip } from "../ui/Tooltip";
import { 
  TrendingUp, 
  DollarSign, 
  Package, 
  Calendar,
  ArrowRight,
  ArrowLeft,
  Target,
  Lightbulb,
  Building2,
  Plus,
  Edit2,
  Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SalesActualsEntry = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext, displayCurrency, convertCurrency } = useCurrency();
  
  // Step state
  const [currentStep, setCurrentStep] = useState(1);
  const [showActualsList, setShowActualsList] = useState(false);
  
  // Data states
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [hierarchy, setHierarchy] = useState(null);
  const [savedActuals, setSavedActuals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUnforeseenProduct, setIsUnforeseenProduct] = useState(false);
  const [showUnforeseenDialog, setShowUnforeseenDialog] = useState(false);
  const [editingActual, setEditingActual] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actualToDelete, setActualToDelete] = useState(null);
  
  // Supporting data for product form
  const [standardProductCategories, setStandardProductCategories] = useState([]);
  const [standardServiceCategories, setStandardServiceCategories] = useState([]);
  const [revenueStreams, setRevenueStreams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  
  const [unforeseenProductForm, setUnforeseenProductForm] = useState({
    sku: "",
    name: "",
    description: "",
    category: "",
    type: "products",
    base_price: "",
    cogs_percentage: "",
    annual_sales_volume_growth: "",
    growth_rates_per_year: {},
    use_per_year_rates: false,
    base_year_units: "",
    active: true,
    launch_year: new Date().getFullYear().toString(),
    end_year: "",
    linked_revenue_stream_id: "",
    owner: ""
  });
  
  // Form states - Date Range Based
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entryMethod, setEntryMethod] = useState("manual"); // manual, automated, imported
  const [dataSource, setDataSource] = useState(""); // POS, Payment Gateway, Salesforce, etc.
  const [unitsSold, setUnitsSold] = useState("");
  const [revenue, setRevenue] = useState("");
  const [notes, setNotes] = useState("");
  
  // Variance states
  const [unitsVariance, setUnitsVariance] = useState(null);
  const [revenueVariance, setRevenueVariance] = useState(null);
  const [avgPrice, setAvgPrice] = useState(null);
  
  // Fetch products on mount
  useEffect(() => {
    if (currentPlan?.id) {
      fetchProducts();
      fetchSavedActuals();
      fetchSupportingData();
      initializeDateRange();
    }
  }, [currentPlan]);
  
  const fetchSupportingData = async () => {
    try {
      // Fetch ALL supporting data using EXACT same endpoints as StrategicFramework
      const [productCatRes, serviceCatRes, streamsRes, teamRes] = await Promise.all([
        axios.get(`${API}/categories/product_categories/${currentPlan.id}`),
        axios.get(`${API}/categories/service_categories/${currentPlan.id}`),
        axios.get(`${API}/v2/revenue-streams/${currentPlan.id}`),
        axios.get(`${API}/enterprise/team-members?plan_id=${currentPlan.id}`)
      ]);
      
      // Use .all not .categories - THIS IS THE KEY!
      setStandardProductCategories(productCatRes.data?.all || []);
      setStandardServiceCategories(serviceCatRes.data?.all || []);
      setRevenueStreams(streamsRes.data || []);
      setTeamMembers(teamRes.data?.team_members || []);
      
      console.log("✅ Product categories:", productCatRes.data?.all);
      console.log("✅ Service categories:", serviceCatRes.data?.all);
      console.log("✅ Revenue streams:", streamsRes.data);
      console.log("✅ Team members:", teamRes.data?.team_members);
      
    } catch (error) {
      console.error("❌ Error fetching supporting data:", error);
      // Set fallback values
      setStandardProductCategories(["Software Products", "Hardware Products", "Digital Products"]);
      setStandardServiceCategories(["Professional Services", "Consulting Services", "Support Services"]);
      setRevenueStreams([]);
      setTeamMembers([]);
    }
  };
  
  const initializeDateRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const firstDay = `${year}-${month}-01`;
    const lastDay = new Date(year, now.getMonth() + 1, 0).toISOString().split('T')[0];
    
    setFromDate(firstDay);
    setToDate(lastDay);
  };
  
  const fetchSavedActuals = async () => {
    try {
      const response = await axios.get(`${API}/actual-data/sales-v2/${currentPlan.id}`);
      setSavedActuals(response.data || []);
    } catch (error) {
      console.error("Error fetching saved actuals:", error);
    }
  };

  
  const handleEditActual = async (actual) => {
    console.log('🔍 Editing actual:', actual);
    console.log('🔍 Actual keys:', Object.keys(actual));
    console.log('🔍 Date fields:', {
      period: actual.period,
      from_date: actual.from_date,
      to_date: actual.to_date,
      date: actual.date
    });
    
    // Handle date fields - could be period, from_date/to_date, or date
    let fromDate = actual.from_date || "";
    let toDate = actual.to_date || "";
    
    // If no from_date/to_date but has period (e.g., "2025-11"), parse it
    if (!fromDate && !toDate && actual.period) {
      console.log('📅 Parsing period:', actual.period);
      // Period format could be "2025-11" or other formats
      if (actual.period.match(/^\d{4}-\d{2}$/)) {
        // Format: YYYY-MM
        fromDate = `${actual.period}-01`; // First day of month
        const year = parseInt(actual.period.substring(0, 4));
        const month = parseInt(actual.period.substring(5, 7));
        const lastDay = new Date(year, month, 0).getDate();
        toDate = `${actual.period}-${lastDay.toString().padStart(2, '0')}`;
      }
    }
    
    // If still no dates but has single date field
    if (!fromDate && actual.date) {
      fromDate = actual.date;
      toDate = actual.date;
    }
    
    console.log('📅 Final dates:', { fromDate, toDate });
    
    // Populate form with existing data
    setFromDate(fromDate);
    setToDate(toDate);
    setEntryMethod(actual.entry_method || "manual");
    setDataSource(actual.data_source || "");
    setUnitsSold(actual.units_sold?.toString() || "");
    setRevenue(actual.revenue?.toString() || "");
    setAvgPrice(actual.average_price?.toString() || "");
    setNotes(actual.notes || "");
    setEditingActual(actual);
    
    // Find and set the product
    const product = products.find(p => p.id === actual.product_id);
    console.log('🔍 Found product:', product);
    
    if (product) {
      setSelectedProduct(product);
      
      // Fetch the hierarchy for this product
      try {
        const hierarchyData = await fetchProductHierarchy(product.id);
        setHierarchy(hierarchyData);
        console.log('✅ Hierarchy loaded:', hierarchyData);
      } catch (error) {
        console.error('❌ Failed to load hierarchy:', error);
        toast.error("Failed to load product hierarchy");
      }
    } else {
      console.warn('⚠️ Product not found for id:', actual.product_id);
      toast.error("Product not found. Please select the product again.");
      setSelectedProduct(null);
      setHierarchy(null);
    }
    
    setShowActualsList(false);
    setCurrentStep(2);
  };
  
  const handleDeleteActual = (actual) => {
    setActualToDelete(actual);
    setShowDeleteDialog(true);
  };
  
  const confirmDeleteActual = async () => {
    if (!actualToDelete) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API}/actual-data/sales-v2/${actualToDelete.id}`);
      toast.success("Sales actual deleted successfully!");
      await fetchSavedActuals();
      setShowDeleteDialog(false);
      setActualToDelete(null);
    } catch (error) {
      console.error("Error deleting sales actual:", error);
      toast.error(error.response?.data?.detail || "Failed to delete sales actual");
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateSalesActual = async () => {
    if (!unitsSold || !revenue || !fromDate || !toDate) {
      toast.error("Please enter units, revenue, and date range");
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        from_date: fromDate,
        to_date: toDate,
        entry_method: entryMethod,
        data_source: dataSource || null,
        units_sold: parseFloat(unitsSold),  // Changed from actual_units
        revenue: parseFloat(revenue),        // Changed from actual_revenue
        average_price: avgPrice ? parseFloat(avgPrice) : (parseFloat(revenue) / parseFloat(unitsSold)),  // Changed from actual_price
        notes
      };
      
      await axios.put(`${API}/actual-data/sales-v2/${editingActual.id}`, payload);
      
      toast.success("Sales actual updated successfully!");
      
      await fetchSavedActuals();
      
      // Reset form
      setCurrentStep(1);
      setSelectedProduct(null);
      setEditingActual(null);
      setUnitsSold("");
      setRevenue("");
      setAvgPrice(null);
      setNotes("");
      setDataSource("");
      
    } catch (error) {
      console.error("Error updating sales actual:", error);
      toast.error(error.response?.data?.detail || "Failed to update sales actual");
    } finally {
      setLoading(false);
    }
  };

  
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/v2/products-enhanced/${currentPlan.id}?include_strategic_only=true`);
      setProducts(response.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProductHierarchy = async (productId) => {
    try {
      setLoading(true);
      // Calculate period for planning based on date range
      const monthStr = fromDate.substring(0, 7); // YYYY-MM format
      const response = await axios.get(
        `${API}/v2/strategic-framework/product/${productId}/full-hierarchy`,
        {
          params: { 
            period: monthStr,
            periodicity: "monthly" // Use monthly as base for now
          }
        }
      );
      setHierarchy(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching product hierarchy:", error);
      toast.error("Failed to fetch product hierarchy");
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  const handleProductSelect = async (product) => {
    setSelectedProduct(product);
    setIsUnforeseenProduct(false);
    const hierarchyData = await fetchProductHierarchy(product.id);
    if (hierarchyData) {
      setCurrentStep(2);
    }
  };
  
  const handleUnforeseenProductSelect = () => {
    // Generate sequential SKU
    const timestamp = Date.now();
    const sku = `UNFORESEEN-${timestamp}`;
    
    setUnforeseenProductForm({
      ...unforeseenProductForm,
      sku: sku,
      launch_year: new Date().getFullYear().toString()
    });
    setShowUnforeseenDialog(true);
  };
  
  const handleSaveUnforeseenProduct = (e) => {
    e.preventDefault();
    
    if (!unforeseenProductForm.name || !unforeseenProductForm.base_price) {
      toast.error("Please enter product/service name and price");
      return;
    }
    
    setIsUnforeseenProduct(true);
    setSelectedProduct(null);
    setHierarchy({
      product: {
        ...unforeseenProductForm,
        id: null,
        sku: unforeseenProductForm.sku || `UNFORESEEN-${Date.now()}`
      },
      revenue_stream: null,
      initiative: null,
      objective: null,
      planning: {
        periodicity: "monthly",
        period: fromDate.substring(0, 7),
        planned_units: 0,
        planned_revenue: 0,
        adjusted_units: 0,
        adjusted_revenue: 0,
        calculation_method: "Unforeseen - No plan exists"
      }
    });
    
    setShowUnforeseenDialog(false);
    setCurrentStep(2);
  };
  
  const getDaysInRange = () => {
    if (!fromDate || !toDate) return 0;
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const diffTime = Math.abs(to - from);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };
  
  const getPeriodLabel = () => {
    const days = getDaysInRange();
    if (days === 1) return "Single Day";
    if (days === 7) return "Weekly";
    if (days >= 28 && days <= 31) return "Monthly";
    if (days >= 89 && days <= 92) return "Quarterly";
    if (days >= 365 && days <= 366) return "Yearly";
    return `${days} Days`;
  };
  
  const calculateVariance = (actual, planned) => {
    if (!planned || planned === 0) return 0;
    return (((actual - planned) / planned) * 100).toFixed(2);
  };
  
  useEffect(() => {
    // Real-time variance calculation
    if (hierarchy && unitsSold && revenue) {
      const plannedUnits = hierarchy.planning?.adjusted_units || 0;
      const plannedRevenue = hierarchy.planning?.adjusted_revenue || 0;
      
      // Get product currency - if not set, assume it's in display currency
      // This prevents incorrect conversion when currency is not explicitly set
      const productCurrency = hierarchy.product?.currency;
      
      // Only convert if product has a different currency than display currency
      // If product currency is not set, we assume the values are already in the user's working currency
      let plannedRevenueForVariance = plannedRevenue;
      let basePriceForVariance = hierarchy.product?.base_price || 0;
      
      if (productCurrency && productCurrency !== displayCurrency) {
        // Product has explicit currency different from display - convert
        plannedRevenueForVariance = convertCurrency(plannedRevenue, productCurrency, displayCurrency);
        basePriceForVariance = convertCurrency(basePriceForVariance, productCurrency, displayCurrency);
      }
      // If productCurrency is not set or matches displayCurrency, use raw values
      
      setUnitsVariance(calculateVariance(parseFloat(unitsSold), plannedUnits));
      setRevenueVariance(calculateVariance(parseFloat(revenue), plannedRevenueForVariance));
      
      if (parseFloat(unitsSold) > 0) {
        setAvgPrice((parseFloat(revenue) / parseFloat(unitsSold)).toFixed(2));
      } else {
        setAvgPrice(0);
      }
    }
  }, [unitsSold, revenue, hierarchy, displayCurrency, convertCurrency]);
  
  const handleSaveSalesActual = async () => {
    if (!unitsSold || !revenue || !fromDate || !toDate) {
      toast.error("Please enter units sold, revenue, and date range");
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        plan_id: currentPlan.id,
        product_id: isUnforeseenProduct ? null : selectedProduct.id,
        from_date: fromDate,
        to_date: toDate,
        entry_method: entryMethod,
        data_source: dataSource || null,
        units_sold: parseFloat(unitsSold),
        revenue: parseFloat(revenue),
        revenue_currency: displayCurrency,  // Store the currency revenue was entered in
        notes,
        is_unforeseen: isUnforeseenProduct,
        unforeseen_product: isUnforeseenProduct ? unforeseenProductForm : null
      };
      
      const response = await axios.post(`${API}/actual-data/sales-v2`, payload);
      
      toast.success("Sales actual saved successfully!");
      
      // Refresh actuals list
      await fetchSavedActuals();
      
      // Reset form
      setCurrentStep(1);
      setSelectedProduct(null);
      setHierarchy(null);
      setIsUnforeseenProduct(false);
      setUnforeseenProductForm({
        sku: "",
        name: "",
        description: "",
        category: "",
        type: "products",
        base_price: "",
        cogs_percentage: "",
        annual_sales_volume_growth: "",
        growth_rates_per_year: {},
        use_per_year_rates: false,
        base_year_units: "",
        active: true,
        launch_year: new Date().getFullYear().toString(),
        end_year: "",
        linked_revenue_stream_id: "",
        owner: ""
      });
      setUnitsSold("");
      setRevenue("");
      setNotes("");
      setDataSource("");
      setUnitsVariance(null);
      setRevenueVariance(null);
      setAvgPrice(null);
      
    } catch (error) {
      console.error("Error saving sales actual:", error);
      toast.error(error.response?.data?.detail || "Failed to save sales actual");
    } finally {
      setLoading(false);
    }
  };
  
  const getPeriodicityLabel = (value) => {
    const labels = {
      realtime: "Realtime",
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      quarterly: "Quarterly",
      yearly: "Yearly"
    };
    return labels[value] || value;
  };
  
  const formatCurrency = (amount, sourceCurrency = null) => {
    // Use product's currency if available, otherwise plan currency
    const currency = sourceCurrency || currentPlan?.currency || 'USD';
    return formatCurrencyContext(amount || 0, currency, null, false);
  };
  
  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Date Range Selection */}
      <Card>
        <CardHeader>
          <Tooltip content="Sales Period (Date Range) - Specify the time period these actual sales represent. Can be any duration: Single day (daily actuals for high-frequency tracking), Week (weekly sales cycles), Month (most common - aligns with accounting periods and board reporting), Quarter (quarterly revenue recognition), Custom range (any specific period like promotion duration, product launch window). Why date range matters: (1) PERIOD ALIGNMENT: Must match your planning periods to calculate accurate variances. If you planned monthly, enter actuals monthly. (2) REVENUE TIMING: Date range determines WHEN revenue is recognized in your performance tracking. Impacts monthly/quarterly metrics and trends. (3) PLAN COMPARISON: System automatically compares your actuals to planned targets for the SAME date range. Mismatched dates = meaningless variance! (4) REPORTING GRANULARITY: Shorter periods (daily/weekly) = More granular insights but more data entry. Longer periods (monthly/quarterly) = Less effort, broader view. Best practices: Monthly actuals = Sweet spot for most businesses (balances detail vs effort, aligns with accounting). Day 1-Last day of month = Clean period boundaries, easier reporting. Consistent periods = Always use same duration (all months, or all weeks) for trend comparability. Close books fast = Enter actuals within 3-5 days of period end while data is fresh. Example: Recording January sales → From Date: Jan 1, To Date: Jan 31 → System will compare to your January plan targets → Variance shows if you beat or missed January goals. Use cases: Monthly revenue recognition (record actuals for each month), Quarterly board reporting (sum up 3 months of actuals), Campaign performance (record actuals for exact campaign dates: Nov 15 - Dec 15), Product launch tracking (record actuals from launch date forward).">
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-blue-600" />
              Sales Period (Date Range)
            </CardTitle>
          </Tooltip>
          <CardDescription>Specify the date range this sales data represents</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Tooltip content="From Date (Start Date) - First day of the sales period you're recording. This is when the sales period BEGINS. Examples: Monthly actuals: Use 1st of the month (Jan 1, Feb 1, Mar 1). Weekly actuals: Use Monday (start of week). Quarterly actuals: Use first day of quarter (Jan 1, Apr 1, Jul 1, Oct 1). Custom periods: Use actual start date (e.g., Black Friday start Nov 24). Critical rules: MUST be on or after your plan start date (can't record actuals before you had a plan). MUST be before or equal to To Date (start can't be after end - logic check). SHOULD align with your planning period boundaries (if plan is monthly, start on month boundaries). Best practice: Use consistent start dates for all entries (always 1st of month) to enable easy trend analysis and period comparison. Calendar selection shows: Max date = To Date (can't select start after end). Disabled dates = Dates outside your business plan timeframe. Example: Recording January 2024 actuals → From Date = 2024-01-01. Recording Q1 2024 actuals → From Date = 2024-01-01 (first day of Q1).">
                <Label>From Date *</Label>
              </Tooltip>
              <Input 
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                max={toDate}
              />
            </div>
            
            <div>
              <Tooltip content="To Date (End Date) - Last day of the sales period you're recording. This is when the sales period ENDS. Examples: Monthly actuals: Use last day of month (Jan 31, Feb 28/29, Mar 31). Weekly actuals: Use Sunday (end of week). Quarterly actuals: Use last day of quarter (Mar 31, Jun 30, Sep 30, Dec 31). Custom periods: Use actual end date (e.g., Cyber Monday end Nov 27). Critical rules: MUST be on or after From Date (end can't be before start). MUST be on or before TODAY (can't record future actuals - that's a forecast, not actuals!). SHOULD align with your planning period boundaries (if plan is monthly, end on month boundaries). Period duration: Days in period = (To Date - From Date) + 1. Example: Jan 1 to Jan 31 = 31 days. Use case implications: Longer periods = Less data entry (one entry per month vs 30 per day) but less granular insights. Shorter periods = More effort but better trend visibility (see daily sales patterns). Variance calculation uses proportional time weighting if partial periods. Best practices: Month-end close = Always end on last day of accounting month for clean financial reporting. Consistent durations = All periods same length (all 1-month, or all 1-week) for comparability. Quick entry = Select current month-to-date quickly (From = 1st of month, To = Today). Example: Recording January 2024 actuals → To Date = 2024-01-31 (last day of January). Recording week of Nov 20-26 → To Date = 2024-11-26 (Sunday).">
                <Label>To Date *</Label>
              </Tooltip>
              <Input 
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                min={fromDate}
              />
            </div>
          </div>
          
          {fromDate && toDate && (
            <div className="bg-blue-50 rounded p-3">
              <p className="text-sm font-medium text-blue-900">
                Period: {getPeriodLabel()} ({getDaysInRange()} days)
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {new Date(fromDate).toLocaleDateString()} - {new Date(toDate).toLocaleDateString()}
              </p>
            </div>
          )}
          
          <div className="border-t pt-4 space-y-3">
            <div>
              <Tooltip content="Entry Method - How was this sales data collected and entered into the system? Understanding data collection method is critical for: Data accuracy assessment (manual more prone to typos, automated more reliable), Audit trails (automated has system logs, manual needs documentation), Time savings (automated eliminates manual data entry work), Data freshness (real-time automated vs delayed manual entry). 3 Entry Methods: (1) MANUAL ENTRY (Default): You type numbers directly into this form. Use when: Low transaction volume (a few sales per period, manageable to type). No automated systems available (early stage business, simple operations). Ad-hoc corrections (fixing one-off data issues). PROS: Full control, no integration needed. CONS: Time-consuming, error-prone (typos), delayed (enter after period ends). (2) AUTOMATED (Real-time): System automatically syncs sales from connected platform (POS, payment gateway, e-commerce). Use when: High transaction volume (hundreds/thousands of transactions - can't manually enter). Real-time visibility needed (want live dashboard updates, not delayed). Integrated systems available (Stripe, Shopify, Salesforce already in use). PROS: Zero manual effort, real-time data, 100% accuracy. CONS: Requires technical integration, dependency on external system uptime. (3) IMPORTED FROM SYSTEM: You export data from another system (CSV, Excel) and import here. Use when: Existing system has data but no API integration (legacy systems). Periodic bulk import (weekly/monthly exports from accounting software). One-time historical data migration (bringing in past actuals). PROS: Faster than manual re-entry, maintains source system as truth. CONS: Still manual effort (export/import), not real-time, file format compatibility issues. Best practices: Start manual (fastest to begin), automate later (as volume grows). Always specify method for audit trail transparency. Document data source (where did numbers come from?). Validate automated data (spot-check automated imports against source system). Strategic implications: Manual = Suitable for <$1M revenue businesses with simple operations. Automated = Required for >$5M revenue or high-velocity businesses. Mixed = Many businesses use mix (automated for main channels, manual for other). Maturity progression: Stage 1 (MVP): Manual entry, focus on process. Stage 2 (Growth): Start automating highest-volume channels. Stage 3 (Scale): Fully automated, real-time data pipeline.">
                <Label>Entry Method</Label>
              </Tooltip>
              <Select value={entryMethod} onValueChange={setEntryMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Entry</SelectItem>
                  <SelectItem value="automated">Automated (Real-time)</SelectItem>
                  <SelectItem value="imported">Imported from System</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {entryMethod !== "manual" && (
              <div>
                <Tooltip content="Data Source - Name of the system or platform where this sales data originated. This is your SOURCE OF TRUTH for sales actuals. Why specify data source: (1) AUDIT TRAIL: Know where every number came from (critical for financial audits and compliance). (2) DATA RECONCILIATION: Match actuals here against source system reports. Example: Stripe says $50K revenue → Check if actuals entry shows Stripe as source and $50K amount. (3) TROUBLESHOOTING: If numbers look wrong, know which system to investigate. (4) MULTI-CHANNEL TRACKING: If selling through multiple channels, source identifies which channel. (5) INTEGRATION MAPPING: Helps technical team build automation to connect these systems. Common data sources by business type: E-COMMERCE: Shopify (online store platform), WooCommerce (WordPress e-commerce), BigCommerce, Magento. Amazon Seller Central (Amazon marketplace), eBay Seller Hub (eBay marketplace). PAYMENT PROCESSORS: Stripe (payment gateway and API), PayPal (payments), Square (POS and payments), Braintree. POINT OF SALE (Retail): Square POS, Clover, Toast (restaurant POS), Lightspeed (retail POS), Vend. CRM / SALES SOFTWARE: Salesforce (enterprise CRM), HubSpot (CRM and sales), Pipedrive, Zoho CRM. ACCOUNTING SOFTWARE: QuickBooks (SMB accounting), Xero (cloud accounting), NetSuite (enterprise ERP), FreshBooks. SUBSCRIPTION PLATFORMS: Chargebee (subscription billing), Recurly, Stripe Billing. MARKETPLACE AGGREGATORS: Shopify (aggregates data from multiple sales channels). Best practices: Use official system name (exactly as it appears - 'Stripe' not 'stripe payment thing'). Be specific for multi-instance (e.g., 'Stripe - US Account' vs 'Stripe - EU Account'). Consistent naming (always use same name for same source across all entries). Document unusual sources (if importing from custom system, note what it is). Validation tips: Source system report = $50,523 revenue for Jan → Actuals entry for Jan from that source = Should match $50,523. Variance investigation: If actuals don't match source, common causes: Date range mismatch (source report Q1, but you entered Jan+Feb only). Refunds handling (source includes refunds, you entered gross without refunds). Currency differences (source in EUR, you converted to USD - note conversion). Fees deduction (source shows gross $50K, you entered net $48K after processing fees). Multi-source scenarios: If selling through 3 channels (website, Amazon, retail), create 3 separate actuals entries: Entry 1: Source = 'Shopify', Units = 500, Revenue = $25K. Entry 2: Source = 'Amazon Seller Central', Units = 300, Revenue = $18K. Entry 3: Source = 'Retail POS - Square', Units = 200, Revenue = $15K. Total actuals = 1,000 units, $58K revenue across 3 sources. This enables channel-level performance analysis later!">
                  <Label>Data Source (Optional)</Label>
                </Tooltip>
                <Input 
                  value={dataSource}
                  onChange={(e) => setDataSource(e.target.value)}
                  placeholder="e.g., POS System, Payment Gateway, Salesforce, etc."
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Product Selection */}
      <Card>
        <CardHeader>
          <Tooltip content="Select Product/Service - Choose which specific product or service generated these sales. This is THE MOST CRITICAL FIELD because it links your actuals to your entire strategic framework! Why product selection matters: (1) STRATEGIC LINKAGE: Product → Revenue Stream → Initiative → Objective. Your sales roll up to strategic goals! (2) PLAN COMPARISON: System pulls planned targets for THIS product for THIS date range to calculate variance. Wrong product = meaningless variance! (3) PERFORMANCE ANALYSIS: All revenue analysis (by product, by stream, by initiative) depends on accurate product selection. (4) PROFITABILITY: Product selection links to cost actuals (via product COGS) for profit calculation. Understanding the product list: This dropdown shows ALL products/services from your Strategic Framework that are: Active (not discontinued), Within date range (launched before your 'From Date', not ended before your 'To Date'), Linked to a revenue stream (products must have revenue attribution). If product is missing from list: Check if product is marked Active in Strategic Framework → Products section. Check product Launch Year (can't select products that haven't launched yet). Check product End Year (can't select products that were discontinued before this period). Verify product has Linked Revenue Stream (products without revenue stream won't appear). Product details shown: SKU = Stock Keeping Unit, unique identifier (e.g., 'PROD-001', 'SVC-CONSULT'). Name = Customer-facing product name (e.g., 'Premium Subscription', 'Consulting Services'). Category = Product classification (helps aggregate analysis, e.g., 'Physical Products', 'Digital Services'). Base Price = Planned price per unit from Strategic Framework (your target selling price). Linked revenue stream, initiative, objective = Strategic hierarchy this product belongs to. Strategic framework connection: When you select a product, system automatically shows you: OBJECTIVE it supports (e.g., 'Grow Revenue to $10M'). INITIATIVE delivering it (e.g., 'Product Launch Campaign'). REVENUE STREAM it generates (e.g., 'Subscription Revenue'). This visibility ensures you understand how this product sale contributes to overall strategy! Use cases: Standard products: Select from dropdown if product was planned. Most common scenario. Unforeseen products: If you sold something you DIDN'T plan for (new opportunity, unplanned offering), click '+ Add Unforeseen Product'. Creates new product on-the-fly. Multi-product entries: If you sold MULTIPLE different products in same period, you need SEPARATE actuals entries for each. Example: Sold Product A (100 units, $10K) AND Product B (200 units, $25K) in January → Create 2 entries: Entry 1 for Product A, Entry 2 for Product B. This enables product-level performance analysis. Best practices: One product per entry (don't combine multiple products in single entry - breaks analysis). Consistent selection (always select same product for same item - don't switch between SKUs). Check hierarchy (verify product links to correct revenue stream/initiative before recording actuals). Update framework first (if new product, add to Strategic Framework → Products BEFORE entering actuals). Example: You're recording January subscription sales → Select 'Premium Subscription (MONTHLY-PREM)' from dropdown → System shows: Objective: Achieve $5M ARR, Initiative: Subscription Growth, Stream: Recurring Revenue, Base Price: $99/mo → Enter actual units sold (150 subscribers) and revenue ($14,850) → System calculates variance vs planned 120 subscribers / $11,880.">
            <CardTitle className="flex items-center">
              <Package className="h-5 w-5 mr-2 text-purple-600" />
              Select Product/Service
            </CardTitle>
          </Tooltip>
          <CardDescription>Choose the product or service for which you're recording sales</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading products...</p>
            </div>
          ) : (
            <>
              {/* Unforeseen Product Option */}
              <div 
                className="border-2 border-dashed border-orange-400 rounded-lg p-4 mb-4 cursor-pointer hover:bg-orange-50 transition-all"
                onClick={handleUnforeseenProductSelect}
              >
                <div className="flex items-center">
                  <Plus className="h-6 w-6 text-orange-600 mr-3" />
                  <div>
                    <h3 className="font-semibold text-orange-900">Record Unforeseen Product/Service Sales</h3>
                    <p className="text-sm text-orange-700">Enter sales for products/services not in your plan (custom projects, emergency orders, pilot products)</p>
                  </div>
                </div>
              </div>
              
              {/* Planned Products */}
              {products.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No strategically linked products found</p>
                  <p className="text-sm text-gray-500 mt-2">You can still record unforeseen sales using the option above</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Planned Products/Services:</p>
                  {products.map((product) => (
                <div
                  key={product.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedProduct?.id === product.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                  onClick={() => handleProductSelect(product)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="font-semibold text-lg">{product.name}</h3>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {product.type === 'services' ? 'Service' : 'Product'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        SKU: {product.sku} | Price: {formatCurrency(product.base_price, product.currency)}
                        {product.owner && ` | Owner: ${product.owner}`}
                      </p>
                      
                      {/* Strategic Framework Linkage */}
                      {(product.revenue_stream_info || product.initiative_info || product.objective_info) && (
                        <div className="mt-2 text-sm text-blue-600">
                          <div className="flex items-center">
                            <Target className="h-3 w-3 mr-1" />
                            <span>Strategy: </span>
                          </div>
                          <div className="ml-4 text-xs space-y-1 mt-1">
                            {product.objective_info && (
                              <div>{product.objective_info.objective_id} ({product.objective_info.objective_name})</div>
                            )}
                            {product.initiative_info && (
                              <div>→ {product.initiative_info.initiative_id} ({product.initiative_info.initiative_name})</div>
                            )}
                            {product.revenue_stream_info && (
                              <div>→ {product.revenue_stream_info.stream_id} ({product.revenue_stream_info.stream_name})</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductSelect(product);
                      }}
                    >
                      Select
                    </Button>
                  </div>
                </div>
              ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
  
  const renderStep2 = () => (
    <div className="space-y-6">
      {/* Period Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <div>
                <span className="text-gray-600">Period:</span>
                <span className="ml-2 font-semibold">{getPeriodLabel()} ({getDaysInRange()} days)</span>
              </div>
              <div>
                <span className="text-gray-600">From:</span>
                <span className="ml-2 font-semibold">{fromDate}</span>
              </div>
              <div>
                <span className="text-gray-600">To:</span>
                <span className="ml-2 font-semibold">{toDate}</span>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setCurrentStep(1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Change
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Strategic Framework Context */}
      <Card>
        <CardHeader>
          <Tooltip content="Shows the complete strategic hierarchy this product belongs to - from high-level objectives down to this specific product. This links your sales data to strategic goals.">
            <CardTitle className="flex items-center">
              <Target className="h-5 w-5 mr-2 text-blue-600" />
              Strategic Framework Context
            </CardTitle>
          </Tooltip>
          <CardDescription>Complete strategic hierarchy for {selectedProduct?.name}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 bg-gray-50 rounded-lg p-4">
            {/* Objective */}
            {hierarchy?.objective && (
              <div className="border-l-4 border-blue-500 pl-4">
                <div className="flex items-center mb-1">
                  <Building2 className="h-4 w-4 mr-2 text-blue-600" />
                  <Tooltip content="Top-level strategic objective this product contributes to" showIcon={false}>
                    <span className="font-semibold">Objective: {hierarchy.objective.name}</span>
                  </Tooltip>
                  <Badge variant="outline" className="ml-2 text-xs">{hierarchy.objective.objective_id}</Badge>
                </div>
                <div className="text-sm text-gray-600">
                  Owner: {hierarchy.objective.owner || 'N/A'} | Pillar: {hierarchy.objective.pillar || 'N/A'}
                </div>
              </div>
            )}
            
            {/* Initiative */}
            {hierarchy?.initiative && (
              <div className="border-l-4 border-green-500 pl-4 ml-4">
                <div className="flex items-center mb-1">
                  <Lightbulb className="h-4 w-4 mr-2 text-green-600" />
                  <Tooltip content="Specific initiative or project that delivers this product" showIcon={false}>
                    <span className="font-semibold">Initiative: {hierarchy.initiative.name}</span>
                  </Tooltip>
                  <Badge variant="outline" className="ml-2 text-xs">{hierarchy.initiative.initiative_id}</Badge>
                </div>
                <div className="text-sm text-gray-600">
                  Owner: {hierarchy.initiative.owner || 'N/A'} | Budget: {formatCurrency(hierarchy.initiative.budget)}
                </div>
                {hierarchy.initiative.timeline_start && (
                  <div className="text-xs text-gray-500">
                    Timeline: {new Date(hierarchy.initiative.timeline_start).toLocaleDateString()} - {new Date(hierarchy.initiative.timeline_end).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}
            
            {/* Revenue Stream */}
            {hierarchy?.revenue_stream && (
              <div className="border-l-4 border-purple-500 pl-4 ml-8">
                <div className="flex items-center mb-1">
                  <DollarSign className="h-4 w-4 mr-2 text-purple-600" />
                  <Tooltip content="Revenue stream category this product generates income through" showIcon={false}>
                    <span className="font-semibold">Revenue Stream: {hierarchy.revenue_stream.name}</span>
                  </Tooltip>
                  <Badge variant="outline" className="ml-2 text-xs">{hierarchy.revenue_stream.stream_id}</Badge>
                </div>
                <div className="text-sm text-gray-600">
                  Owner: {hierarchy.revenue_stream.owner || 'N/A'} | Type: {hierarchy.revenue_stream.type}
                </div>
              </div>
            )}
            
            {/* Product */}
            <div className="border-l-4 border-orange-500 pl-4 ml-12">
              <div className="flex items-center mb-1">
                <Package className="h-4 w-4 mr-2 text-orange-600" />
                <Tooltip content="The specific product/service you're recording sales for" showIcon={false}>
                  <span className="font-semibold">Product: {hierarchy?.product.name}</span>
                </Tooltip>
                <Badge variant="outline" className="ml-2 text-xs">{hierarchy?.product.sku}</Badge>
                <Badge variant="outline" className="ml-2 text-xs">{hierarchy?.product.type}</Badge>
              </div>
              <div className="text-sm text-gray-600">
                Owner: {hierarchy?.product.owner || 'N/A'} | Price: {formatCurrency(hierarchy?.product.base_price)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Plan vs Actual Comparison */}
      <Card>
        <CardHeader>
          <Tooltip content="Plan vs Actual Entry - This is where actuals meet planning! Compare what you PLANNED to achieve against what you ACTUALLY achieved. This comparison is the foundation of performance management. Why Plan vs Actual matters: (1) ACCOUNTABILITY: Did you hit your targets? This shows execution against commitments. (2) LEARNING: Understanding variances helps improve future planning (were targets too aggressive? Too conservative?). (3) COURSE CORRECTION: Large negative variances trigger corrective actions. Positive variances show what's working. (4) FORECASTING: Actual vs Plan patterns help refine future forecasts. Layout explanation: LEFT SIDE (Planned): Your original targets from strategic planning. These don't change - they're your baseline. Shows: Units planned, Revenue planned, Avg Price (base price). Source: Strategic Framework → Product Planning for this product/period. RIGHT SIDE (Actual): Real numbers you enter. This is what ACTUALLY happened. Enter: Units Sold, Revenue ($$), Avg Price auto-calculates from your entries. BOTTOM (Variance): Automatic real-time calculation showing over/under performance. Green positive % = Beat plan (exceeded targets!). Red negative % = Missed plan (fell short of targets). Three variance metrics: Units Variance = Volume performance. Revenue Variance = Financial performance. Price Variance = Pricing performance. Understanding variance components: TOTAL REVENUE VARIANCE = VOLUME VARIANCE + PRICE VARIANCE + MIX VARIANCE. This entry focuses on single product, so: If both Units and Revenue positive = Sold more units AND generated more revenue (excellent!). If Units positive but Revenue negative = Sold more units but at lower prices (volume up, pricing pressure). If Units negative but Revenue positive = Sold fewer units but at higher prices (lost volume, premium positioning). If both negative = Sold fewer units and generated less revenue (investigate urgently!). Use cases: Monthly performance reviews: 'Did we hit January targets? Where did we miss?' KPI tracking: Revenue variance is key KPI for sales team performance. Root cause analysis: Variance highlights WHERE to investigate (units? pricing?). Bonus calculation: Sales team bonuses often tied to hitting plan (+/- variance threshold). Best practices: Plan is sacred: Don't change plan retroactively to make variance look better. Honest actuals: Enter true numbers, even if ugly (better to know reality than hide issues). Explain variances: Use Notes field to document WHY variance occurred (market shift, campaign success, supply shortage). Regular cadence: Enter actuals monthly within 5 days of period end for timely insights. Example interpretation: Planned: 1,000 units, $100K revenue, $100 avg price. Actual: 1,200 units, $108K revenue, $90 avg price. Variance: Units +20% (GREAT! Sold 20% more than planned), Revenue +8% (GOOD! But only 8% more revenue despite 20% more units - why?), Price -10% (CONCERN! Average price 10% lower than planned - discounting? Lower-priced product mix?). Analysis: Strong volume growth but pricing pressure. Action: Investigate why avg price dropped (promotional discounting? Customer negotiations? Product mix shift to lower-priced variants?). Consider if volume gain worth price sacrifice.">
            <CardTitle className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
              Plan vs Actual Entry
            </CardTitle>
          </Tooltip>
          <CardDescription>Enter actual sales data and compare with planning</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Currency Info Banner */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <DollarSign className="h-5 w-5 text-blue-600 mt-0.5" />
              </div>
              <div className="ml-3 flex-1">
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  💱 Multi-Currency Support Active
                </h4>
                <p className="text-sm text-blue-800">
                  <strong>Current Display Currency: {displayCurrency}</strong>. All planned values are automatically converted from the product's native currency to {displayCurrency} for easy comparison. Enter your actual revenue in {displayCurrency}. Variance calculations are currency-aware and accurate regardless of product's original currency.
                </p>
                <p className="text-xs text-blue-700 mt-2">
                  <strong>Test it:</strong> Try products priced in different currencies (USD, ETB, EUR) - the system handles conversion automatically!
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Planned Data */}
            <div>
              <Tooltip content="Planned Targets - Your original goals from strategic planning. These numbers come from Strategic Framework → Product Planning for this specific product and period. Shows your commitments/targets you set to achieve. Components: UNITS = Quantity target. How many units you planned to sell. Example: 1,000 subscriptions, 500 consulting hours, 250 physical products. Source: Product planning with growth rates applied. REVENUE = Dollar target. How much money you planned to generate. Formula: Units × Base Price (with any period adjustments). Example: 1,000 units × $100 = $100,000 planned revenue. AVG PRICE = Expected price per unit. Your target selling price. Source: Product Base Price from Strategic Framework. Example: $99 for monthly subscription. Calculation Method shown below = How these planned numbers were derived (growth rate applied, static target, seasonal adjustment, etc.). Why planned numbers shown: BASELINE FOR COMPARISON: Can't calculate variance without knowing what was planned. ACCOUNTABILITY: These are your commitments - did you deliver? CONTEXT: Helps interpret actuals (is $80K revenue good? Depends if plan was $100K or $60K!). Important notes: Planned numbers are PRORATED to your date range. If you planned $100K for full month (30 days) but entering actuals for 15 days, shown plan = $50K (50% of month). This ensures fair variance calculation. Planned numbers DON'T change when you enter actuals. They're your fixed baseline. If planned numbers look wrong: Check product planning in Strategic Framework → Products section. Verify growth rates and base year units. Check if period calculation is correct (monthly vs quarterly vs annual proration). If you need to change plan: Update Strategic Framework → Product Planning, then re-enter actuals (variance will recalculate against new plan).">
                <h3 className="font-semibold mb-4 flex items-center text-gray-700">
                  📋 Planned (Base: Monthly)
                </h3>
              </Tooltip>
              <div className="space-y-3 bg-green-50 rounded-lg p-4">
                <div>
                  <Tooltip content="Planned number of units to sell in this period">
                    <Label className="text-sm text-gray-600">Units</Label>
                  </Tooltip>
                  <div className="text-2xl font-bold text-green-700">
                    {hierarchy?.planning?.adjusted_units?.toLocaleString() || 0}
                  </div>
                </div>
                <div>
                  <Tooltip content="Planned revenue target for this period. Displayed in your current Display Currency for easy comparison with actuals. Formula: Planned Units × Product Base Price (in product's currency) → Converted to Display Currency. Example: Product priced at 100,000 ETB, planned 10 units = 1,000,000 ETB planned revenue. If Display Currency is USD (rate 120:1), shows as $8,333.33 for comparison.">
                    <Label className="text-sm text-gray-600">Revenue</Label>
                  </Tooltip>
                  <div className="text-2xl font-bold text-green-700">
                    {formatCurrency(hierarchy?.planning?.adjusted_revenue, hierarchy?.product.currency)}
                  </div>
                </div>
                <div>
                  <Tooltip content="Expected average selling price per unit. Displayed in your current Display Currency. This is the product's base price converted to display currency for easy comparison. Example: Product base price 50,000 ETB, Display Currency USD (rate 120:1) → Shows as $416.67">
                    <Label className="text-sm text-gray-600">Avg Price</Label>
                  </Tooltip>
                  <div className="text-lg font-semibold text-green-700">
                    {formatCurrency(hierarchy?.product.base_price, hierarchy?.product.currency)}
                  </div>
                </div>
                {hierarchy?.planning?.calculation_method && (
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <Label className="text-xs text-gray-600">Calculation Method</Label>
                    <p className="text-xs text-gray-700 mt-1">{hierarchy.planning.calculation_method}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Actual Data Entry */}
            <div>
              <Tooltip content="Actual Sales (Entry Fields) - Enter the real numbers from your actual sales for this period. This is TRUTH - what really happened in the business. Where these numbers come from: (1) TRANSACTION SYSTEMS: POS reports, Payment gateway (Stripe/PayPal), E-commerce platform (Shopify), Accounting software (QuickBooks). (2) MANUAL RECORDS: If early stage, may be manual sales log, invoices issued, bank deposits. (3) REPORTS: Sales reports from CRM, Order fulfillment reports, Revenue recognition schedules. Data accuracy is CRITICAL because: All performance analysis, variance reporting, and trend analysis depends on accurate actuals. Inaccurate actuals = Wrong decisions (garbage in, garbage out!). Financial reporting may depend on these numbers. Two required fields: UNITS SOLD and REVENUE. Third field (Avg Price) auto-calculates.">
                <h3 className="font-semibold mb-4 flex items-center text-gray-700">
                  ✍️ Actual (Enter)
                </h3>
              </Tooltip>
              <div className="space-y-4">
                <div>
                  <Tooltip content="Units Sold (Actual Quantity) - Total number of units you ACTUALLY sold during this period. Units = Quantity measure specific to your product type. Examples by product type: Physical products: Number of items sold. Example: 1,500 t-shirts, 327 laptops. Subscriptions: Number of active subscriptions. Example: 1,200 monthly subscribers, 340 annual subscribers. Services: Hours delivered or projects completed. Example: 450 consulting hours, 23 projects. Digital: Downloads, licenses, access granted. Example: 5,432 app downloads, 890 software licenses. What to count: Count ALL units sold in period, regardless of: When ordered (if delivered in period), When paid (revenue recognition may differ from payment timing), Channel (add up all channels: online + retail + wholesale). Count ONLY units where: Product/service was delivered in this period (not just ordered), Revenue was recognized (accounting rules for your business). Don't count: Refunds/returns (subtract these from units), Cancelled orders (if not delivered), Free trials (unless your model counts them). Data sources: Transaction records: Count from POS, e-commerce platform order exports. Fulfillment logs: Units shipped/delivered. Service logs: Hours logged, projects delivered. Subscription metrics: Active subscriber count at period end. Accuracy tips: Cross-check against: Inventory (sold units = beginning inventory - ending inventory + purchases). Revenue (does units × price roughly equal revenue? If not, investigate). Payment records (units sold should correlate with payment transactions). Common mistakes: Double-counting: Same sale recorded twice in system. Period mismatch: Including sales from wrong date range. Returns included: Forgot to subtract returns/refunds. Strategic importance: Units Sold drives: Volume variance (are you selling the quantity you planned?). Market share analysis (your units sold vs market size). Capacity planning (if selling more units than planned, need more capacity!). Pricing analysis (compare units vs revenue to understand pricing dynamics). Best practices: Be precise: Use exact numbers, not rounded estimates. Enter promptly: Record within 3-5 days while data fresh. Document source: Note where numbers came from in Notes field. Reconcile: Periodically verify actuals sum to accounting records. Example: January subscription business: Start with 1,000 subscribers, Added 150 new sign-ups, Lost 50 cancellations, End with 1,100 subscribers. But Units Sold for Jan = NEW subscriptions = 150 (not 1,100!). Unless your model tracks total active subscriptions, not new adds.">
                    <Label>Units Sold *</Label>
                  </Tooltip>
                  <Input 
                    type="number"
                    value={unitsSold}
                    onChange={(e) => setUnitsSold(e.target.value)}
                    placeholder="Enter units sold"
                    className="text-lg"
                  />
                </div>
                <div>
                  <Tooltip content="IMPORTANT: Enter revenue in YOUR CURRENT DISPLAY CURRENCY shown in the header dropdown. The system automatically handles currency conversion for variance calculations. Example: If Display Currency is USD, enter revenue in dollars (e.g., $10,000). If ETB, enter in Birr (e.g., 1,200,000). Formula: Generally Units Sold × Price. System stores which currency you entered so it can properly convert for future viewing.">
                    <Label>Revenue ({displayCurrency}) *</Label>
                  </Tooltip>
                  <Input 
                    type="number"
                    value={revenue}
                    onChange={(e) => setRevenue(e.target.value)}
                    placeholder="Enter revenue"
                    className="text-lg"
                  />
                </div>
                <div>
                  <Tooltip content="Auto-calculated: Revenue ÷ Units Sold. Displayed in your current Display Currency. Formula: Avg Price = Total Revenue / Total Units. Example: $10,000 revenue ÷ 100 units = $100.00 avg price. This shows your actual selling price and is compared to planned base price to calculate Price Variance.">
                    <Label>Avg Price (auto-calculated)</Label>
                  </Tooltip>
                  <div className="text-lg font-semibold text-blue-700 bg-blue-50 rounded px-3 py-2">
                    {avgPrice ? formatCurrency(parseFloat(avgPrice), displayCurrency) : formatCurrency(0, displayCurrency)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Variance Preview */}
          {(unitsVariance !== null || revenueVariance !== null) && (
            <div className="mt-6 p-4 bg-purple-50 rounded-lg">
              <Tooltip content="Variance Preview (Real-Time) - Automatic calculation showing how your ACTUAL performance compares to PLANNED targets. Updates instantly as you type! This is your performance scorecard. Variance = (Actual - Planned) / Planned × 100. Shows percentage over/under performance. Positive % (green) = FAVORABLE = Exceeded plan, beat targets! Negative % (red) = UNFAVORABLE = Missed plan, fell short of targets. Zero % = Perfect execution, hit exactly on target (rare!). Three variance metrics shown: (1) Units Variance = Volume performance, (2) Revenue Variance = Financial performance (MOST IMPORTANT!), (3) Price Variance = Pricing performance. Strategic framework for variance analysis: SMALL VARIANCE (±5%): Generally acceptable, within normal business fluctuations. May not require action unless part of concerning trend. MODERATE VARIANCE (5-15%): Warrants investigation. Understand root causes. May need tactical adjustments. LARGE VARIANCE (>15%): Requires immediate attention! Deep root cause analysis needed. May need strategic changes or corrective actions. Variance interpretation patterns: ALL THREE POSITIVE (Units, Revenue, Price all green): EXCELLENT! Perfect execution, exceeded targets across the board. All cylinders firing! Action: Document success factors, replicate approach, celebrate team! UNITS POSITIVE, REVENUE POSITIVE, PRICE NEGATIVE: Strong volume growth but pricing pressure. Sold way more units but at lower prices. Net effect: More revenue overall but margin concern. Action: Assess if volume gain justifies price sacrifice. May be intentional (market share grab) or unintentional (excessive discounting). UNITS NEGATIVE, REVENUE NEGATIVE, PRICE POSITIVE: Volume shortfall despite premium pricing. Sold fewer units but at higher prices. Possible: Lost price-sensitive customers, focused on premium segment. Action: Decide strategy - lower prices to regain volume? Or maintain premium positioning? UNITS POSITIVE, REVENUE NEGATIVE: Sold more units but LESS revenue. Mathematically requires price to be significantly lower. Major pricing issue! Action: Urgent pricing review. Are you discounting too heavily? REVENUE VARIANCE COMPONENTS: Total Revenue Variance = Volume Effect + Price Effect. Volume Effect = (Actual Units - Planned Units) × Planned Price. Price Effect = (Actual Price - Planned Price) × Actual Units. Understanding this decomposition helps: If revenue variance is -10%, how much is due to volume? How much due to pricing? This guides corrective actions (need to sell more units? Or need to improve pricing?). Use cases by stakeholder: SALES TEAM: Units and Revenue variance = Performance scorecard. Bonus calculations often tied to revenue variance thresholds. FINANCE/CFO: Revenue variance = Key input to forecast updates. Significant variance = Reforecast rest of year. EXECUTIVE TEAM: Pattern of variances = Strategic signals. Consistent negative = Strategy not working. BOARD/INVESTORS: Revenue variance trends = Execution capability indicator. Improving variance over time = Getting better at execution. Worsening = Losing grip. Best practices: Enter actuals regularly (monthly) to track variance trends over time (not just one-time snapshot). Investigate all moderate+ variances (>5%). Don't let them pile up without understanding root causes. Document variance explanations in Notes field (why did we miss/beat? Market shift? Campaign success? Supply issue?). Use variances to improve planning (if consistently missing by -20%, your planning assumptions may be too aggressive). Don't manipulate numbers to make variance look good (integrity matters - better to know truth!). Example interpretation: Variance shows: Units Variance: +15% (green), Revenue Variance: +8% (green), Price Variance: -6% (red). Translation: Sold 15% more units than planned (great volume growth!), Generated 8% more revenue (beat target, but not proportional to volume growth - why?), Average price 6% below plan (pricing pressure or mix shift to lower-priced items). Net assessment: POSITIVE performance (beat revenue target!) but with nuance (pricing concern). Action items: Celebrate volume success (what drove it? Campaign? Market growth? Replicate!), Investigate pricing gap (why lower avg price? Intentional discounts? Product mix? Competitive pressure?), Calculate if volume gain offsets price reduction (did profit margin decline despite higher revenue?), Decide on pricing strategy going forward (can we raise prices now that we have volume? Or maintain discount to keep growth?).">
                <h3 className="font-semibold mb-3 flex items-center text-gray-700">
                  📈 Variance Preview (Real-time)
                </h3>
              </Tooltip>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Tooltip content="Units Variance Formula: ((Actual Units - Planned Units) / Planned Units) × 100. Example: Actual 120 units, Planned 100 units → (120-100)/100 × 100 = +20%. Green (positive) = Sold more than planned. Red (negative) = Sold less than planned.">
                    <Label className="text-sm text-gray-600">Units Variance</Label>
                  </Tooltip>
                  <div className={`text-xl font-bold ${
                    parseFloat(unitsVariance) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {unitsVariance}%
                  </div>
                </div>
                <div>
                  <Tooltip content="Revenue Variance Formula (CURRENCY-AWARE): ((Actual Revenue - Planned Revenue in Display Currency) / Planned Revenue in Display Currency) × 100. The system automatically converts planned revenue to your Display Currency before comparison. Example: Product in ETB, Display Currency USD. Planned 1M ETB (converts to $8,333 USD), Actual $10,000 USD → (10000-8333)/8333 × 100 = +20%. Green = Beat target! Red = Missed target.">
                    <Label className="text-sm text-gray-600">Revenue Variance</Label>
                  </Tooltip>
                  <div className={`text-xl font-bold ${
                    parseFloat(revenueVariance) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {revenueVariance}%
                  </div>
                </div>
                <div>
                  <Tooltip content="Price Variance Formula (CURRENCY-AWARE): ((Actual Avg Price - Planned Base Price in Display Currency) / Planned Base Price in Display Currency) × 100. The system converts product's base price to Display Currency before comparison. Example: Product base 100,000 ETB (converts to $833 USD), Actual avg $900 USD → (900-833)/833 × 100 = +8%. Green = Sold at premium. Red = Sold at discount.">
                    <Label className="text-sm text-gray-600">Price Variance</Label>
                  </Tooltip>
                  <div className={`text-xl font-bold ${
                    avgPrice && hierarchy?.product.base_price
                      ? (() => {
                          const productCurrency = hierarchy.product?.currency;
                          // Only convert if product has explicit currency different from display
                          const basePriceInDisplayCurrency = productCurrency && productCurrency !== displayCurrency
                            ? convertCurrency(hierarchy.product.base_price, productCurrency, displayCurrency)
                            : hierarchy.product.base_price;
                          return (parseFloat(avgPrice) - basePriceInDisplayCurrency) >= 0 ? 'text-green-600' : 'text-red-600';
                        })()
                      : 'text-gray-600'
                  }`}>
                    {avgPrice && hierarchy?.product.base_price
                      ? (() => {
                          const productCurrency = hierarchy.product?.currency;
                          // Only convert if product has explicit currency different from display
                          const basePriceInDisplayCurrency = productCurrency && productCurrency !== displayCurrency
                            ? convertCurrency(hierarchy.product.base_price, productCurrency, displayCurrency)
                            : hierarchy.product.base_price;
                          return calculateVariance(parseFloat(avgPrice), basePriceInDisplayCurrency);
                        })()
                      : 0}%
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Notes */}
          <div className="mt-6">
            <Label>Notes (Optional)</Label>
            <Textarea 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add contextual notes about this sales entry..."
              rows={3}
            />
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t">
            <Button 
              variant="outline" 
              onClick={() => setCurrentStep(1)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button 
              onClick={editingActual ? handleUpdateSalesActual : handleSaveSalesActual}
              disabled={loading || !unitsSold || !revenue}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? "Saving..." : editingActual ? "Update Actual" : "Save Actual"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
  
  const renderActualsList = () => (
    <Card>
      <CardHeader>
        <CardTitle>Recorded Sales Actuals</CardTitle>
        <CardDescription>All sales actuals recorded for this plan</CardDescription>
      </CardHeader>
      <CardContent>
        {savedActuals.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No sales actuals recorded yet</p>
            <Button className="mt-4" onClick={() => setShowActualsList(false)}>
              <Plus className="h-4 w-4 mr-2" />
              Record First Actual
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {savedActuals.map((actual, index) => (
              <div key={actual.id || index} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="font-semibold text-lg">{actual.product_name}</h3>
                      <Badge variant="outline" className="ml-2">{actual.product_sku}</Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Period:</span>
                        <p className="font-medium">
                          {actual.from_date && actual.to_date 
                            ? `${new Date(actual.from_date).toLocaleDateString()} - ${new Date(actual.to_date).toLocaleDateString()}`
                            : actual.period || actual.date
                          }
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Units Sold:</span>
                        <p className="font-medium">{actual.units_sold?.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Revenue:</span>
                        <p className="font-medium">{formatCurrency(actual.revenue, actual.revenue_currency || actual.product_currency)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Avg Price:</span>
                        <p className="font-medium">{formatCurrency(actual.average_price, actual.revenue_currency || actual.product_currency)}</p>
                      </div>
                    </div>
                    
                    {(actual.units_variance_pct !== null || actual.revenue_variance_pct !== null) && (
                      <div className="mt-3 flex items-center space-x-4 text-sm">
                        <div>
                          <span className="text-gray-600">Units Variance:</span>
                          <span className={`ml-2 font-semibold ${
                            actual.units_variance_pct >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {actual.units_variance_pct}%
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Revenue Variance:</span>
                          <span className={`ml-2 font-semibold ${
                            actual.revenue_variance_pct >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {actual.revenue_variance_pct}%
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {actual.notes && (
                      <p className="mt-2 text-sm text-gray-600 italic">{actual.notes}</p>
                    )}
                    
                    <p className="mt-2 text-xs text-gray-500">
                      Recorded: {new Date(actual.created_at).toLocaleString()}
                      {actual.entry_method && ` | Entry: ${actual.entry_method}`}
                      {actual.data_source && ` | Source: ${actual.data_source}`}
                    </p>
                  </div>
                  
                  {/* Edit and Delete Buttons */}
                  <div className="flex flex-col space-y-2 ml-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEditActual(actual)}
                      className="flex items-center"
                    >
                      <Edit2 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDeleteActual(actual)}
                      className="flex items-center text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Tooltip 
            content="Record your actual sales performance data to track progress against strategic plans and financial targets. This data powers your dashboards and variance analysis."
            position="bottom"
          >
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <DollarSign className="h-8 w-8 mr-3 text-green-600" />
              Record Sales Actuals
            </h1>
          </Tooltip>
          <p className="text-gray-600 mt-2">
            {showActualsList 
              ? `Viewing ${savedActuals.length} recorded actuals` 
              : currentStep === 1 
                ? "Select product and configure date range" 
                : "Enter actual sales data and compare with planning"
            }
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          {!showActualsList && (
            <>
              <Tooltip content="View all previously recorded sales actuals. You can edit or delete past entries.">
                <Button 
                  variant="outline" 
                  onClick={() => setShowActualsList(true)}
                >
                  View Recorded Actuals ({savedActuals.length})
                </Button>
              </Tooltip>
              {/* Step Indicator */}
              <div className="flex items-center space-x-2 ml-4">
                <Tooltip content="Step 1: Select a product/service and date range for recording actuals">
                  <Badge variant={currentStep === 1 ? "default" : "outline"}>Step 1</Badge>
                </Tooltip>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <Tooltip content="Step 2: Enter actual sales figures and view variance against your plan">
                  <Badge variant={currentStep === 2 ? "default" : "outline"}>Step 2</Badge>
                </Tooltip>
              </div>
            </>
          )}
          {showActualsList && (
            <Tooltip content="Start recording a new sales actual entry">
              <Button onClick={() => setShowActualsList(false)}>
                <Plus className="h-4 w-4 mr-2" />
                Record New Actual
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
      
      {/* Current Step Content or Actuals List */}
      {showActualsList ? renderActualsList() : (currentStep === 1 ? renderStep1() : renderStep2())}
      
      {/* Unforeseen Product Dialog - Uses SF Product Form */}
      <ProductFormDialog
        open={showUnforeseenDialog}
        onOpenChange={setShowUnforeseenDialog}
        productForm={unforeseenProductForm}
        setProductForm={setUnforeseenProductForm}
        onSubmit={handleSaveUnforeseenProduct}
        standardProductCategories={standardProductCategories}
        standardServiceCategories={standardServiceCategories}
        revenueStreams={revenueStreams}
        teamMembers={teamMembers}
        isUnforeseen={true}
      />
      
      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center text-red-600">
                <Trash2 className="h-5 w-5 mr-2" />
                Delete Sales Actual
              </CardTitle>
              <CardDescription>
                Are you sure you want to delete this sales actual entry? This action cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {actualToDelete && (
                <div className="py-4 space-y-2">
                  <p className="font-semibold">{actualToDelete.product_name}</p>
                  <p className="text-sm text-gray-600">
                    Period: {actualToDelete.from_date && actualToDelete.to_date 
                      ? `${new Date(actualToDelete.from_date).toLocaleDateString()} - ${new Date(actualToDelete.to_date).toLocaleDateString()}`
                      : actualToDelete.period || actualToDelete.date
                    }
                  </p>
                  <p className="text-sm text-gray-600">
                    Units: {actualToDelete.units_sold?.toLocaleString()} | Revenue: {formatCurrency(actualToDelete.revenue, actualToDelete.revenue_currency || actualToDelete.product_currency)}
                  </p>
                </div>
              )}
              
              <div className="flex justify-end space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setActualToDelete(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmDeleteActual}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {loading ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SalesActualsEntry;
